import React, { useState, useEffect } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Autocomplete, TextField, Checkbox, createFilterOptions } from "@mui/material";
import { IoIosArrowBack } from "react-icons/io";
import { format } from "date-fns";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useFetchGetAllProjectBasedUserNameListQuery } from "../../../slice/logdetailSlice";
import { API } from "../../../apis/apiService";
import { toast } from "react-toastify";
import { useFetchUserAccessedProjectsQuery, useFetchIssueModuleQuery } from "../../../slice/logdetailSlice";

const filter = createFilterOptions({ limit: 100 });

export const CommonIssueExport = ({ onCancel, refetchList, IssuesList, isModule }) => {
  const { id: project_id_param } = useParams();
  const project_id = isModule ? null : project_id_param;
  const [loading, setLoading] = useState(false);
  useSelector((state) => state.selectOptions || {});


  // const { data: allProjectsData } = 
  useFetchUserAccessedProjectsQuery(undefined, {
    skip: !isModule,
  });
  const { data: globalIssueData, isLoading: isGlobalIssueLoading } = useFetchIssueModuleQuery(
    { view_type_id: 1 },
    { skip: !isModule }
  );

  // const dataSource = isModule ? globalIssueData : IssuesList;

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      Projects: "",
      Issues: [],
      StartDate: null,
      EndDate: null,
      billingType: "",
      Reporter: [],
      Assignee: [],
      format: "excel",
    },
  });

  const onSubmit = async (data) => {
    const requestedData = {
      project_id: isModule ? (data.Projects ? Number(data.Projects) : null) : (project_id ? Number(project_id) : null),
      users: [],
      issues: data.Issues || [],
      from_date: data.StartDate ? format(new Date(data.StartDate), "yyyy-MM-dd") : null,
      end_date: data.EndDate ? format(new Date(data.EndDate), "yyyy-MM-dd") : null,
      billing_type: data.billingType,
      reporter: (data.Reporter && data.Reporter.length > 0) ? Number(data.Reporter[0]) : null,
      assignee: (data.Assignee && data.Assignee.length > 0) ? Number(data.Assignee[0]) : null,
      format: data.format,
    };

    setLoading(true);
    try {
      const response = await API.post("issue/export", requestedData);
      if (response?.data?.status && response?.data?.fileUrl) {
        const link = document.createElement("a");
        link.href = response.data.fileUrl;
        link.setAttribute("download", "");
        document.body.appendChild(link);
        link.click();
        link.remove();

        onCancel();
        toast.success(response.data.message || "Export successful.");
      } 
      else {
        throw new Error(response?.data?.message || "The response does not contain valid export data.");
      }
    } 
    catch (error) {
      console.error("Error exporting issues:", error);
      let errorMsg = "Export failed";
      if (error.response && error.response.data) {
        errorMsg = error.response.data.message || error.response.data.error || errorMsg;
      } 
      else if (error.message) {
        errorMsg = error.message;
      }
      toast.error(errorMsg);
    } 
    finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    reset();
  };

  const selectedProjectId = useWatch({
    control,
    name: "Projects",
    defaultValue: "",
  });

  const selectedIssueId = useWatch({
    control,
    name: "Issues",
    defaultValue: [],
  });

  const { data: projectUsers, isLoading: isProjectUsersLoading } = useFetchGetAllProjectBasedUserNameListQuery(
    isModule ? selectedProjectId : project_id,
    {
      skip: isModule ? !selectedProjectId : !project_id,
    }
  );

  const isProjectsLoading = isModule ? isGlobalIssueLoading : false;
  const isIssuesLoading = isModule ? isGlobalIssueLoading : !IssuesList;
  const isReporterLoading = isModule ? (selectedProjectId ? isProjectUsersLoading : isGlobalIssueLoading) : isProjectUsersLoading;
  const isAssigneeLoading = isModule ? (selectedProjectId ? isProjectUsersLoading : isGlobalIssueLoading) : isProjectUsersLoading;

  useEffect(() => {
      setValue("Issues", []);
      setValue("Reporter", []);
      setValue("Assignee", []);
  }, [selectedProjectId, setValue]);

  const selectedIssueIdString = JSON.stringify(selectedIssueId);

  useEffect(() => {
      setValue("Reporter", []);
      setValue("Assignee", []);
  }, [selectedIssueIdString, setValue]);

  // 1. Flatten all issues once to avoid expensive flatMap inside loops
  const allIssuesFlat = React.useMemo(() => {
    return isModule
      ? globalIssueData?.flatMap((item) => item.issues || []) || []
      : IssuesList || [];
  }, [isModule, globalIssueData, IssuesList]);

  const filteredData = isModule
    ? globalIssueData?.filter((item) => 
        !selectedProjectId || Number(item.project?.project_id) === Number(selectedProjectId)
      )
    : IssuesList;

  const reporterOptions = React.useMemo(() => {
    if (selectedIssueId && selectedIssueId.length > 0) {
      const usersMap = new Map();
      selectedIssueId.forEach((issueId) => {
        const selectedIssueObj = allIssuesFlat.find(
          (issue) => Number(issue.issue_id || issue.id) === Number(issueId)
        );

        if (selectedIssueObj) {
          const repName = selectedIssueObj.reporter || selectedIssueObj.reporter_name;
          const repId = selectedIssueObj.added_by || selectedIssueObj.reporter_id || (repName ? projectUsers?.find(
            (u) => u.name?.toLowerCase() === repName.toLowerCase()
          )?.user_id : null);
          if (repId && repName) {
            usersMap.set(repId, { id: repId, title: repName });
          } else if (repName) {
            usersMap.set(repName, { id: repName, title: repName });
          }
        }
      });
      return Array.from(usersMap.values());
    }

    const usersMap = new Map();
    if (isModule) {
      filteredData?.forEach((item) => {
        item.issues?.forEach((issue) => {
          const rId = issue.reporter_id || issue.added_by;
          const rName = issue.reporter_name || issue.reporter;
          if (rId && rName) {
            usersMap.set(rId, {
              id: rId,
              title: rName,
            });
          } else if (rName) {
            usersMap.set(rName, {
              id: rName,
              title: rName,
            });
          }
        });
      });
      return Array.from(usersMap.values());
    } 
    else {
      IssuesList?.forEach((issue) => {
        const rId = issue.reporter_id || issue.added_by;
        const rName = issue.reporter || issue.reporter_name;
        if (rId && rName) {
          usersMap.set(rId, {
            id: rId,
            title: rName,
          });
        } else if (rName) {
          usersMap.set(rName, {
            id: rName,
            title: rName,
          });
        }
      });
      return Array.from(usersMap.values());
    }
  }, [selectedIssueId, allIssuesFlat, isModule, filteredData, IssuesList, projectUsers]);

  const assigneeOptions = React.useMemo(() => {
    if (selectedIssueId && selectedIssueId.length > 0) {
      const usersMap = new Map();
      selectedIssueId.forEach((issueId) => {
        const selectedIssueObj = allIssuesFlat.find(
          (issue) => Number(issue.issue_id || issue.id) === Number(issueId)
        );

        if (selectedIssueObj) {
          const assName = selectedIssueObj.assignee || selectedIssueObj.assignee_name;
          const assId = selectedIssueObj.assignee_id || (assName ? projectUsers?.find(
            (u) => u.name?.toLowerCase() === assName.toLowerCase()
          )?.user_id : null);
          if (assId && assName) {
            usersMap.set(assId, { id: assId, title: assName });
          } else if (assName) {
            usersMap.set(assName, { id: assName, title: assName });
          }
        }
      });
      return Array.from(usersMap.values());
    }

    const usersMap = new Map();
    if (isModule) {
      filteredData?.forEach((item) => {
        item.issues?.forEach((issue) => {
          const assName = issue.assignee_name || issue.assignee;
          if (assName) {
            const matchedUser = projectUsers?.find(
              (u) => u.name?.toLowerCase() === assName.toLowerCase()
            );
            const assId = issue.assignee_id || matchedUser?.user_id;
            if (assId) {
              usersMap.set(assId, {
                id: assId,
                title: assName,
              });
            } else {
              usersMap.set(assName, {
                id: assName,
                title: assName,
              });
            }
          }
        });
      });
      return Array.from(usersMap.values());
    } 

    else {
      IssuesList?.forEach((issue) => {
        const assName = issue.assignee || issue.assignee_name;
        const assId = issue.assignee_id || (assName ? projectUsers?.find(
          (u) => u.name?.toLowerCase() === assName.toLowerCase()
        )?.user_id : null);
        if (assId && assName) {
          usersMap.set(assId, {
            id: assId,
            title: assName,
          });
        } else if (assName) {
          usersMap.set(assName, {
            id: assName,
            title: assName,
          });
        }
      });
      return Array.from(usersMap.values());
    }
  }, [selectedIssueId, allIssuesFlat, isModule, filteredData, IssuesList, projectUsers]);

  const issueOptions = React.useMemo(() => {
    return isModule
      ? filteredData?.flatMap((item) =>
          item.issues?.map((issue) => ({
            id: issue.issue_id,
            title: issue.title,
          }))
        ) || []
      : IssuesList?.map((issue) => ({
          id: issue.issue_id || issue.id,
          title: issue.title,
        })) || [];
  }, [isModule, filteredData, IssuesList]);

  const projectOptions = React.useMemo(() => {
    return isModule
      ? globalIssueData?.map((item) => ({
          project_id: item.project?.project_id,
          title: item.project?.title,
        })).filter((project, index, self) => 
          project.project_id && self.findIndex(p => p.project_id === project.project_id) === index
        ) || []
      : [];
  }, [isModule, globalIssueData]);

  return (
    <div className="h-100 d-flex flex-column">
      <div className="d-flex align-items-center bg-white justify-content-between px-4 py-3">
        <button onClick={onCancel} className="R_btn">
          <div className="d-flex text-rgba-gray align-items-center">
            <IoIosArrowBack className="text-rgba-gray" />
            Export Issues
          </div>
        </button>
        <button type="button" onClick={handleClear} className="R_btn tex-tgba-red clear-filter-btn">
          Clear Filter
        </button>
      </div>

      <div className="flex-grow-1 overflow-auto bg-white px-4 py-3">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row g-3">
              {isModule && (
                <div className="col-12">
                  <label className="form-label">Projects <span className="text-danger">*</span></label>
                  <Controller
                    name="Projects"
                    control={control}
                    rules={{ required: "Project is required" }}
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        options={projectOptions}
                        loading={isProjectsLoading}
                        loadingText="Loading..."
                        noOptionsText="No data found"
                        getOptionLabel={(option) => option.title || ""}
                        value={projectOptions.find((p) => p.project_id === value) || null}
                        onChange={(e, newValue) => {
                          onChange(newValue ? newValue.project_id : "");
                        }}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} className="d-flex align-items-center mb-2">
                            <Checkbox
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            <strong>{option.title}</strong>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select project" />
                        )}
                      />
                    )}
                  />
                  {errors?.Projects && (
                    <span className="errorMsg">{errors.Projects.message}</span>
                  )}
                </div>
              )}

              <div className="col-12">
                <label className="form-label">Issues</label>
                <Controller
                  name="Issues"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    const selectedValues = issueOptions.filter((opt) =>
                      (value || []).includes(opt.id)
                    );
                    return (
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={issueOptions}
                        loading={isIssuesLoading}
                        loadingText="Loading..."
                        noOptionsText="No data found"
                        filterOptions={filter}
                        getOptionLabel={(option) => option.title || ""}
                        value={selectedValues}
                        onChange={(e, newValue) => {
                          onChange(newValue.map((val) => val.id));
                        }}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} className="d-flex align-items-center mb-2">
                            <Checkbox
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            <strong>{option.title}</strong>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select issues" />
                        )}
                      />
                    );
                  }}
                />
              </div>

              <div className="col-12">
                <label className="form-label">Reporter</label>
                <Controller
                  name="Reporter"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    const selectedValues = reporterOptions.filter((opt) =>
                      (value || []).includes(opt.id)
                    );
                    return (
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={reporterOptions}
                        loading={isReporterLoading}
                        loadingText="Loading..."
                        noOptionsText="No data found"
                        getOptionLabel={(option) => option.title || ""}
                        value={selectedValues}
                        onChange={(e, newValue) => {
                          onChange(newValue.map((val) => val.id));
                        }}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} className="d-flex align-items-center mb-2">
                            <Checkbox
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            <strong>{option.title}</strong>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select users" />
                        )}
                      />
                    );
                  }}
                />
              </div>

              <div className="col-12">
                <label className="form-label">Assignee</label>
                <Controller
                  name="Assignee"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    const selectedValues = assigneeOptions.filter((opt) =>
                      (value || []).includes(opt.id)
                    );
                    return (
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={assigneeOptions}
                        loading={isAssigneeLoading}
                        loadingText="Loading..."
                        noOptionsText="No data found"
                        getOptionLabel={(option) => option.title || ""}
                        value={selectedValues}
                        onChange={(e, newValue) => {
                          onChange(newValue.map((val) => val.id));
                        }}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} className="d-flex align-items-center mb-2">
                            <Checkbox
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            <strong>{option.title}</strong>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select assignee" />
                        )}
                      />
                    );
                  }}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Start date <span className="text-danger">*</span></label>
                <Controller
                  name="StartDate"
                  control={control}
                  rules={{ required: "Start date is required" }}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      format="DD/MM/YYYY"
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  )}
                />
                {errors?.StartDate && (
                  <span className="errorMsg">{errors.StartDate.message}</span>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">End date <span className="text-danger">*</span></label>
                <Controller
                  name="EndDate"
                  control={control}
                  rules={{ required: "End date is required" }}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      format="DD/MM/YYYY"
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  )}
                />
                {errors?.EndDate && (
                  <span className="errorMsg">{errors.EndDate.message}</span>
                )}
              </div>
            </div>
            
            <div className="d-flex bg-white py-3 mt-4 bottom-0 ">
              <button 
                type="submit" 
                className="add_btn me-3" 
                disabled={loading}
              >
                {loading ? "Exporting..." : "Apply"}
              </button>
              <button 
                type="button" 
                className="cancel_btn" 
                onClick={onCancel}
              >
                Cancel
              </button>
            </div>
          </form>
        </LocalizationProvider>
      </div>
    </div>
  );
};
